import { computeLogicalViewport } from './compute-logical-viewport'
import { computePlaceholderRect } from './compute-placeholder-rect'
import { buildChartIframeUrl, redactSensitiveUrl } from './safe-diagnostics'
import type { CssRect } from './types'

export interface ChartViewAdapterLogger {
  warn(message: string, details?: unknown): void
  log(message: string, details?: unknown): void
}

export interface ChartViewAdapterDependencies {
  logger: ChartViewAdapterLogger
  /** Base chart URL the iframe loads. Matches `ChartView`'s hard-coded PlayCanvas deployment URL in production; overridable for the harness. */
  chartBaseUrl: string
  /**
   * When set, `show()` loads this URL unchanged instead of constructing one from
   * `chartBaseUrl` + the harness's placeholder tournament/player/ticket/wsBaseUrl args.
   * Lets a tester supply a fully authenticated chart URL (with a real ticket) for
   * touch/pan/pinch validation, without the adapter mangling or re-authenticating it.
   */
  fullIframeUrlOverride?: string
}

/**
 * In-repository port of the supplied PlayCanvas `ChartView` implementation.
 * Preserves its iframe creation, positioning, symbol-update, polling, and
 * destruction behavior so the harness exercises the real integration
 * algorithm rather than a re-derived approximation.
 */
export class ChartViewAdapter {
  private iframe?: HTMLIFrameElement
  private canvas?: HTMLCanvasElement
  private updateIntervalId?: number

  private lastLeft = -1
  private lastTop = -1
  private lastWidth = -1
  private lastHeight = -1

  /**
   * The chart URL, held unassigned until valid geometry resolves. Navigation is deferred
   * so DXChart's first layout always runs against desktop-capable logical dimensions;
   * loading it into a 0x0 iframe is exactly the narrow-viewport initialization this
   * contract exists to prevent. Cleared once assigned.
   */
  private pendingSrc?: string

  constructor(private readonly dependencies: ChartViewAdapterDependencies) {}

  public show(symbol: string, tournamentId: string, playerId: string, ticket: string, wsBaseUrl: string): void {
    this.destroy()

    this.canvas = document.querySelector('canvas') ?? undefined

    if (!this.canvas) {
      this.dependencies.logger.warn('[ChartViewAdapter] Canvas not found.')
      return
    }

    const iframe = document.createElement('iframe')

    // Held, not assigned: `updatePosition()` navigates once it has real dimensions.
    this.pendingSrc = this.dependencies.fullIframeUrlOverride
      ? this.dependencies.fullIframeUrlOverride
      : buildChartIframeUrl(this.dependencies.chartBaseUrl, {
          tournamentId,
          playerId,
          ticket,
          wsBaseUrl,
          symbol,
        })

    iframe.style.position = 'fixed'

    iframe.style.left = '0px'
    iframe.style.top = '0px'

    iframe.style.width = '0px'
    iframe.style.height = '0px'

    iframe.style.border = 'none'
    iframe.style.margin = '0'
    iframe.style.padding = '0'

    iframe.style.transform = 'none'
    // Top-left origin makes the transformed bounds start at the requested rectangle's
    // top-left, so no positional compensation is needed for the scale.
    iframe.style.transformOrigin = 'top left'

    iframe.style.zIndex = '9999'

    // `visibility` rather than `display`: `display: none` removes the iframe from layout,
    // collapsing its content box to zero. The inner document survives, but it observes a
    // zero-size viewport, which is exactly the narrow-layout measurement this contract
    // avoids — DXChart would then re-lay-out on every hide/restore cycle.
    // `visibility: hidden` keeps the box (and its logical dimensions) in layout and only
    // stops painting it. `pointer-events: none` stops the invisible box eating input.
    iframe.style.visibility = 'hidden'
    iframe.style.pointerEvents = 'none'

    document.body.appendChild(iframe)

    this.iframe = iframe

    this.lastLeft = -1
    this.lastTop = -1
    this.lastWidth = -1
    this.lastHeight = -1

    this.dependencies.logger.log('[ChartViewAdapter] IFRAME CREATED')
    this.dependencies.logger.log('[ChartViewAdapter] Chart URL:', redactSensitiveUrl(this.pendingSrc))

    this.updatePosition()

    this.startUpdateLoop()
  }

  public setSymbol(symbol: string): void {
    if (!this.iframe) return

    // While navigation is still deferred the symbol belongs on the pending URL; rewriting
    // `iframe.src` there would navigate early, at the wrong viewport size.
    const current = this.pendingSrc ?? this.iframe.src

    if (!current) return

    const url = new URL(current)

    url.searchParams.set('symbol', symbol.replace('/', ''))

    if (this.pendingSrc) {
      this.pendingSrc = url.toString()
    } else {
      this.iframe.src = url.toString()
    }
  }

  public update(): void {
    this.updatePosition()
  }

  private startUpdateLoop(): void {
    this.stopUpdateLoop()

    // The chart only needs occasional position updates; 15 FPS is enough for an overlay.
    this.updateIntervalId = window.setInterval(() => {
      if (!this.iframe) return

      this.updatePosition()
    }, 66)
  }

  private stopUpdateLoop(): void {
    if (this.updateIntervalId === undefined) return

    window.clearInterval(this.updateIntervalId)

    this.updateIntervalId = undefined
  }

  private updatePosition(): void {
    const iframe = this.iframe
    const canvas = this.canvas
    const placeholder = window.chartPlaceholder

    if (!iframe || !canvas) return

    if (!placeholder?.element) {
      this.hide()
      return
    }

    const corners = placeholder.element.screenCorners

    const canvasRect = canvas.getBoundingClientRect()

    const rect: CssRect | undefined = computePlaceholderRect(canvasRect, canvas.width, canvas.height, corners)

    if (!rect) {
      this.hide()
      return
    }

    const viewport = computeLogicalViewport(rect.width, rect.height)

    if (!viewport) {
      this.hide()
      return
    }

    const geometryUnchanged =
      rect.left === this.lastLeft &&
      rect.top === this.lastTop &&
      rect.width === this.lastWidth &&
      rect.height === this.lastHeight

    if (!geometryUnchanged) {
      this.lastLeft = rect.left
      this.lastTop = rect.top
      this.lastWidth = rect.width
      this.lastHeight = rect.height

      // The iframe keeps the visual rectangle's top-left and receives desktop-capable
      // logical dimensions; the uniform scale brings its transformed bounds back to the
      // rectangle's width and height. The URL is untouched, so the live DXChart document
      // survives movement, resize, orientation, and restoration updates.
      iframe.style.left = `${rect.left}px`
      iframe.style.top = `${rect.top}px`
      iframe.style.width = `${viewport.logicalWidth}px`
      iframe.style.height = `${viewport.logicalHeight}px`
      iframe.style.transform = `scale(${viewport.scale})`
    }

    // Navigate only now that real logical dimensions are on the element, so DXChart's
    // very first layout sees a desktop-capable viewport. Deferred until after the style
    // writes above, and only ever done once.
    if (this.pendingSrc) {
      iframe.src = this.pendingSrc
      this.pendingSrc = undefined
    }

    // Runs even when geometry is unchanged so a previously hidden iframe is restored.
    if (iframe.style.visibility !== 'visible') {
      iframe.style.visibility = 'visible'
      iframe.style.pointerEvents = 'auto'
    }
  }

  /**
   * Hides the iframe without removing it from layout, so a later valid rectangle restores
   * the same live chart in place without it having measured a zero-size viewport.
   */
  private hide(): void {
    if (!this.iframe) return

    if (this.iframe.style.visibility !== 'hidden') {
      this.iframe.style.visibility = 'hidden'
      this.iframe.style.pointerEvents = 'none'
    }

    // Force the next valid rectangle to re-apply geometry even if it matches the last one.
    this.lastLeft = -1
    this.lastTop = -1
    this.lastWidth = -1
    this.lastHeight = -1
  }

  public destroy(): void {
    this.stopUpdateLoop()

    if (this.iframe) {
      this.iframe.remove()

      this.iframe = undefined
    }

    this.canvas = undefined
    this.pendingSrc = undefined

    this.lastLeft = -1
    this.lastTop = -1
    this.lastWidth = -1
    this.lastHeight = -1
  }
}
